import { Clock, Download, FileText, Play, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import {
  archivedCaptionUrl,
  archivedThumbUrl,
  archivedVideoUrl,
  listRenders,
  restoreRender,
  type JobStatus,
  type RenderArchiveEntry,
} from "../../api";

/**
 * Lists the past renders preserved by the archive system. The current
 * (live) render is intentionally NOT in this list — it's already shown
 * by the main video player. Each row exposes a play link plus a
 * download link, and a one-line summary of the inputs that produced it
 * so the operator can tell two iterations apart at a glance.
 *
 * Polls when the job is rendering so a new archive shows up immediately
 * after a re-run starts; otherwise fetches once on mount.
 *
 * Hosted by the Studio's Edit bar under "Versions" (spec 2026-09-16 §2.4):
 * a host that passes `onRestored` follows the restore itself instead of the
 * page-era full reload, and `empty` gives the group something to say when
 * there is nothing to list yet (the panel used to render nothing).
 */
export function RenderHistoryPanel({
  jobId,
  status,
  onRestored,
  empty,
}: {
  jobId: string;
  status: JobStatus;
  /** Called after a Restore succeeded. Absent ⇒ the page reloads (today's
   *  behaviour: the restored render must replace the player). */
  onRestored?: () => void;
  /** Shown when there are no archived renders. Absent ⇒ renders nothing. */
  empty?: React.ReactNode;
}): React.JSX.Element | null {
  const [items, setItems] = useState<RenderArchiveEntry[] | null>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      try {
        const data = await listRenders(jobId);
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    void refresh();
    // Refresh while rendering so new archives show up live.
    if (status === "rendering" || status === "queued" || status === "clipping" || status === "transcribing" || status === "translating") {
      const id = window.setInterval(refresh, 8000);
      return () => { cancelled = true; window.clearInterval(id); };
    }
    return () => { cancelled = true; };
  }, [jobId, status]);

  if (!items || items.length === 0) {
    if (items && empty !== undefined) {
      return (
        <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.55, padding: "6px 2px" }}>
          {empty}
        </div>
      );
    }
    return null;
  }

  return (
    <div
      style={{
        marginTop: 4,
        padding: 14,
        borderRadius: 14,
        background: "rgba(16,18,27,.72)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <Clock size={13} strokeWidth={2} color="var(--ql-fg-muted)" />
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ql-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: ".10em",
          }}
        >
          Earlier versions · {items.length}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it) => (
          <RenderHistoryRow
            key={it.idx}
            jobId={jobId}
            entry={it}
            playing={activeIdx === it.idx}
            onTogglePlay={() => setActiveIdx((cur) => (cur === it.idx ? null : it.idx))}
            onRestored={onRestored}
          />
        ))}
      </div>
    </div>
  );
}

function RenderHistoryRow({
  jobId,
  entry,
  playing,
  onTogglePlay,
  onRestored,
}: {
  jobId: string;
  entry: RenderArchiveEntry;
  playing: boolean;
  onTogglePlay: () => void;
  onRestored?: () => void;
}): React.JSX.Element {
  const [restoring, setRestoring] = useState(false);
  const handleRestore = async (): Promise<void> => {
    if (restoring) return;
    setRestoring(true);
    try {
      await restoreRender(jobId, entry.idx);
      if (onRestored) {
        onRestored();
        return;
      }
      // Reload so the main player shows the restored render and the history
      // refreshes — the render you rolled back FROM is now the newest entry,
      // and its ↺ rolls forward again.
      window.location.reload();
    } catch {
      setRestoring(false);
    }
  };
  const reasonLabel: Record<RenderArchiveEntry["reason"], string> = {
    reclip: "Re-cut",
    "caption-offset": "Caption shift",
    trim: "Trim",
    "regenerate-captions": "Captions regenerated",
    "remix-audio": "Music re-mixed",
    restore: "Restored",
    "edit-speaker": "Speaker edit",
    rerender: "Re-render",
    "polish-camera": "Camera polish",
    reframe: "Reframe",
    "studio-approve": "Studio design",
  };
  const summaryParts: string[] = [];
  if (entry.input?.from) summaryParts.push(`from ${entry.input.from}`);
  if (entry.input?.to) summaryParts.push(`to ${entry.input.to}`);
  else if (entry.input?.duration !== undefined) summaryParts.push(`${entry.input.duration}s`);
  if (entry.reason === "caption-offset" && typeof entry.context?.offsetMs === "number") {
    const ms = entry.context.offsetMs as number;
    summaryParts.push(`shift ${ms > 0 ? "+" : ""}${ms}ms`);
  }
  if (entry.reason === "remix-audio" && typeof entry.context?.musicVolume === "number") {
    summaryParts.push(`music vol ${(entry.context.musicVolume as number).toFixed(2)}`);
  }
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontFamily: "var(--ql-font-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            background: "rgba(255,213,0,.10)",
            padding: "2px 8px",
            borderRadius: 999,
            border: "1px solid rgba(255,213,0,.20)",
          }}
        >
          v{entry.idx}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ql-fg)" }}>
            {reasonLabel[entry.reason]}
            {summaryParts.length > 0 && (
              <span style={{ fontWeight: 400, color: "var(--ql-fg-muted)" }}>
                {" · "}
                {summaryParts.join(" · ")}
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: "var(--ql-fg-subtle)", marginTop: 2 }}>
            {timeAgo(entry.archivedAt)} ago · {new Date(entry.archivedAt).toLocaleString()}
          </div>
        </div>
        {entry.hasVideo && (
          <button
            type="button"
            onClick={onTogglePlay}
            title={playing ? "Hide preview" : "Play this render"}
            style={archiveBtnStyle(playing)}
          >
            <Play size={13} strokeWidth={1.85} />
          </button>
        )}
        {entry.hasVideo && (
          <a
            href={archivedVideoUrl(jobId, entry.idx)}
            download={`${jobId}-v${entry.idx}.mp4`}
            title="Download mp4"
            style={archiveBtnStyle(false)}
          >
            <Download size={13} strokeWidth={1.85} />
          </a>
        )}
        {entry.hasCaption && (
          <a
            href={archivedCaptionUrl(jobId, entry.idx)}
            target="_blank"
            rel="noreferrer"
            title="View post caption"
            style={archiveBtnStyle(false)}
          >
            <FileText size={13} strokeWidth={1.85} />
          </a>
        )}
        {entry.hasVideo && (
          <button
            type="button"
            onClick={() => { void handleRestore(); }}
            disabled={restoring}
            title="Roll back to this render — your current render is saved to history first (reversible)"
            style={archiveBtnStyle(false)}
          >
            <RotateCcw size={13} strokeWidth={1.85} />
          </button>
        )}
      </div>
      {playing && entry.hasVideo && (
        <video
          controls
          autoPlay
          preload="metadata"
          poster={archivedThumbUrl(jobId, entry.idx)}
          src={archivedVideoUrl(jobId, entry.idx)}
          style={{
            width: "100%",
            marginTop: 10,
            borderRadius: 8,
            background: "#000",
            aspectRatio: "9 / 16",
            maxHeight: 360,
          }}
        />
      )}
    </div>
  );
}

function archiveBtnStyle(active: boolean): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: active ? "1px solid rgba(255,213,0,.30)" : "1px solid rgba(255,255,255,.08)",
    background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
    color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
    transition: "all .15s var(--ql-ease)",
    flexShrink: 0,
  };
}

function timeAgo(ts: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}
